/* eslint-disable react/forbid-dom-props -- dynamic pixel positions from d3 scales */
import React, { useMemo, useState } from 'react'

import { useChartLayout } from '../core/chart-context'
import { TOOLTIP_FALLBACK_BG, TOOLTIP_FALLBACK_COLOR } from './TooltipSurface'

export type ReferenceLineOrientation = 'horizontal' | 'vertical'
export type ReferenceLineVariant = 'goal' | 'alert' | 'marker'
export type ReferenceLineFillSide = 'above' | 'below' | 'left' | 'right'
export type ReferenceLineLabelPosition = 'start' | 'end'
export type ReferenceLineStroke = 'dashed' | 'solid'

export interface ReferenceLineStyle {
    /** CSS color string. Supports `var(--my-color)`. Overrides the variant default. */
    color?: string
    /** Stroke style. Defaults to the variant default. */
    stroke?: ReferenceLineStroke
    /** Line thickness in px. Defaults to the variant default. */
    width?: number
    /** CSS color used for the filled half-plane when `fillSide` is set. Defaults to `color`. */
    fillColor?: string
    /** Opacity 0-1 for the filled half-plane. Defaults to 0.1. */
    fillOpacity?: number
}

export interface ReferenceLineProps {
    /** The axis value at which to draw the line. For horizontal lines this is a numeric
     *  y-value; for vertical lines it's an x-axis label (matching the chart's `labels`). */
    value: number | string
    /** `horizontal` draws across the plot at a y-value (default).
     *  `vertical` draws top-to-bottom at an x-axis label. */
    orientation?: ReferenceLineOrientation
    /** Optional text label rendered alongside the line. */
    label?: string
    /** Anchor the label at the `start` or `end` of the line. Defaults to `end`. */
    labelPosition?: ReferenceLineLabelPosition
    /** Style overrides. Variant picks sensible defaults; anything set here wins. */
    style?: ReferenceLineStyle
    /** Optional filled half-plane on one side of the line. */
    fillSide?: ReferenceLineFillSide
    /** Preset: `goal` (dashed grey), `alert` (dashed red), `marker` (solid thin grey). Defaults to `goal`. */
    variant?: ReferenceLineVariant
    /** Which y-axis this line references. Only used for horizontal lines. Defaults to the primary axis. */
    yAxisId?: string
    /** Chart axis orientation. When `'horizontal'`, a `'horizontal'`-orientation reference
     *  line at a numeric value is drawn as a vertical stripe at `scales.y(value)` — matching
     *  the value axis of horizontal bar charts. Defaults to the chart's own axis orientation
     *  (from context), so it's correct without the caller having to thread it through. */
    axisOrientation?: ReferenceLineOrientation
}

interface ResolvedStyle {
    color: string
    stroke: ReferenceLineStroke
    width: number
}

const VARIANT_DEFAULTS: Record<ReferenceLineVariant, ResolvedStyle> = {
    goal: { color: 'rgba(0, 0, 0, 0.4)', stroke: 'dashed', width: 2 },
    alert: { color: '#db3707', stroke: 'dashed', width: 2 },
    marker: { color: 'rgba(0, 0, 0, 0.5)', stroke: 'solid', width: 1 },
}

/** Approximate rendered height of the label bubble — used only to keep it inside the plot near
 *  the top/bottom edges. Vertical centering itself is exact via a translateY(-50%) transform. */
const LABEL_HEIGHT = 20
/** Padding between the label and the plot edge. */
const LABEL_PADDING = 4
/** Thickness of the invisible hover target centered on the line (height for horizontal lines, width
 *  for vertical). The stroked line itself is only a couple of pixels, so hovering it to reveal the
 *  value would be near-impossible without a wider hit area — this restores the legacy behavior where
 *  hovering anywhere on a goal line surfaces its number, even when the line has no visible label. */
const HOVER_HIT_THICKNESS = 12

function resolveStyle(variant: ReferenceLineVariant, style: ReferenceLineStyle | undefined): ResolvedStyle {
    const defaults = VARIANT_DEFAULTS[variant]
    return {
        color: style?.color ?? defaults.color,
        stroke: style?.stroke ?? defaults.stroke,
        width: style?.width ?? defaults.width,
    }
}

/** Renders a list of reference lines. */
export function ReferenceLines({ lines }: { lines: ReferenceLineProps[] }): React.ReactElement {
    return (
        <>
            {lines.map((props, i) => (
                <ReferenceLine key={`${i}-${props.value}-${props.label ?? ''}`} {...props} />
            ))}
        </>
    )
}

/** Dispatches to the orientation-specific renderer. Each sub-component does its own
 *  type narrowing, scale lookup, and bounds check, then hands pre-computed styles to
 *  {@link ReferenceLineView}. */
export function ReferenceLine(props: ReferenceLineProps): React.ReactElement | null {
    const { axis } = useChartLayout()
    const { orientation = 'horizontal', variant = 'goal', style, axisOrientation = axis.orientation } = props
    const resolved = useMemo(
        () => resolveStyle(variant, style),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [variant, style?.color, style?.stroke, style?.width]
    )

    const common: ResolvedProps = {
        resolved,
        fillSide: props.fillSide,
        fillColor: style?.fillColor ?? resolved.color,
        fillOpacity: style?.fillOpacity ?? 0.1,
        label: props.label,
        labelPosition: props.labelPosition ?? 'end',
        // Numeric lines (goal thresholds) reveal their value on hover; vertical marker lines don't.
        valueText: typeof props.value === 'number' ? props.value.toLocaleString() : undefined,
    }

    if (orientation === 'horizontal') {
        if (typeof props.value !== 'number') {
            return null
        }
        if (axisOrientation === 'horizontal') {
            return <HorizontalAxisValueReferenceLine value={props.value} {...common} />
        }
        return <HorizontalReferenceLine y={props.value} yAxisId={props.yAxisId} {...common} />
    }
    return typeof props.value === 'string' ? <VerticalReferenceLine xLabel={props.value} {...common} /> : null
}

interface ResolvedProps {
    resolved: ResolvedStyle
    fillSide: ReferenceLineFillSide | undefined
    fillColor: string
    fillOpacity: number
    label: string | undefined
    labelPosition: ReferenceLineLabelPosition
    /** Formatted value shown appended to the label on hover. Undefined for non-numeric lines. */
    valueText: string | undefined
}

function HorizontalReferenceLine({
    y: value,
    yAxisId,
    resolved,
    fillSide,
    fillColor,
    fillOpacity,
    label,
    labelPosition,
    valueText,
}: ResolvedProps & { y: number; yAxisId?: string }): React.ReactElement | null {
    const { scales, dimensions } = useChartLayout()
    const { plotLeft, plotTop, plotWidth, plotHeight, width: containerWidth } = dimensions
    const plotRight = plotLeft + plotWidth
    const plotBottom = plotTop + plotHeight

    const yScaleFn = yAxisId && scales.yAxes?.[yAxisId] ? scales.yAxes[yAxisId].scale : scales.y
    const y = yScaleFn(value)
    if (!isFinite(y) || y < plotTop || y > plotBottom) {
        return null
    }

    const lineStyle: React.CSSProperties = {
        left: plotLeft,
        top: y - resolved.width / 2,
        width: plotWidth,
        height: 0,
        borderTopWidth: resolved.width,
        borderTopStyle: resolved.stroke,
        borderTopColor: resolved.color,
    }
    // Center the label bubble on the line (translateY(-50%) handles exact centering regardless of
    // rendered height), but keep its center far enough from the plot edges that an extended-axis
    // goal line sitting at the very top/bottom isn't clipped.
    const labelCenterY = Math.max(plotTop + LABEL_HEIGHT / 2, Math.min(y, plotBottom - LABEL_HEIGHT / 2))
    const labelStyle: React.CSSProperties = {
        top: labelCenterY,
        transform: 'translateY(-50%)',
        ...(labelPosition === 'end'
            ? { right: containerWidth - plotRight + LABEL_PADDING }
            : { left: plotLeft + LABEL_PADDING }),
    }

    let fillRect: React.CSSProperties | null = null
    if (fillSide === 'above') {
        fillRect = { left: plotLeft, top: plotTop, width: plotWidth, height: y - plotTop }
    } else if (fillSide === 'below') {
        fillRect = { left: plotLeft, top: y, width: plotWidth, height: plotBottom - y }
    }

    // Numeric lines reveal their value on hover, so give the line a full-width hover target.
    const hitAreaStyle: React.CSSProperties | null =
        valueText != null
            ? { left: plotLeft, top: y - HOVER_HIT_THICKNESS / 2, width: plotWidth, height: HOVER_HIT_THICKNESS }
            : null

    return (
        <ReferenceLineView
            fillRect={fillRect}
            fillColor={fillColor}
            fillOpacity={fillOpacity}
            lineStyle={lineStyle}
            hitAreaStyle={hitAreaStyle}
            label={label}
            valueText={valueText}
            labelStyle={labelStyle}
        />
    )
}

// Renders a vertical stripe at a resolved x-pixel — used by both the categorical-x
// reference line (string xLabel) and the horizontal-axis-chart numeric variant
// (scales.y(value) returns an x-pixel in horizontal bar charts).
//
// `fillBefore` / `fillAfter` are direction-neutral: callers translate their own
// fillSide enum into these so we don't have to know whether the half-plane is
// "left/right" (categorical) or "below/above value threshold" (horizontal-axis).
function VerticalStripe({
    x,
    resolved,
    fillBefore,
    fillAfter,
    fillColor,
    fillOpacity,
    label,
    labelPosition,
    valueText,
}: Omit<ResolvedProps, 'fillSide'> & {
    x: number
    fillBefore: boolean
    fillAfter: boolean
}): React.ReactElement | null {
    const { dimensions } = useChartLayout()
    const { plotLeft, plotTop, plotWidth, plotHeight, height: containerHeight } = dimensions
    const plotRight = plotLeft + plotWidth
    const plotBottom = plotTop + plotHeight

    if (!isFinite(x) || x < plotLeft || x > plotRight) {
        return null
    }

    const lineStyle: React.CSSProperties = {
        left: x - resolved.width / 2,
        top: plotTop,
        width: 0,
        height: plotHeight,
        borderLeftWidth: resolved.width,
        borderLeftStyle: resolved.stroke,
        borderLeftColor: resolved.color,
    }
    const labelStyle: React.CSSProperties = {
        left: x + LABEL_PADDING,
        ...(labelPosition === 'end'
            ? { bottom: containerHeight - plotBottom + LABEL_PADDING }
            : { top: plotTop + LABEL_PADDING }),
    }

    let fillRect: React.CSSProperties | null = null
    if (fillBefore) {
        fillRect = { left: plotLeft, top: plotTop, width: x - plotLeft, height: plotHeight }
    } else if (fillAfter) {
        fillRect = { left: x, top: plotTop, width: plotRight - x, height: plotHeight }
    }

    // Numeric lines reveal their value on hover, so give the line a full-height hover target.
    const hitAreaStyle: React.CSSProperties | null =
        valueText != null
            ? { left: x - HOVER_HIT_THICKNESS / 2, top: plotTop, width: HOVER_HIT_THICKNESS, height: plotHeight }
            : null

    return (
        <ReferenceLineView
            fillRect={fillRect}
            fillColor={fillColor}
            fillOpacity={fillOpacity}
            lineStyle={lineStyle}
            hitAreaStyle={hitAreaStyle}
            label={label}
            valueText={valueText}
            labelStyle={labelStyle}
        />
    )
}

function VerticalReferenceLine({
    xLabel,
    fillSide,
    ...rest
}: ResolvedProps & { xLabel: string }): React.ReactElement | null {
    const { scales } = useChartLayout()
    const x = scales.x(xLabel)
    if (x == null) {
        return null
    }
    return <VerticalStripe x={x} fillBefore={fillSide === 'left'} fillAfter={fillSide === 'right'} {...rest} />
}

// Horizontal-axis chart variant: numeric value, mapped through scales.y because
// in horizontal bar charts scales.y is the value scale producing x-pixels.
// `'above'` (value above the threshold) → right half-plane, `'below'` → left.
function HorizontalAxisValueReferenceLine({
    value,
    fillSide,
    ...rest
}: ResolvedProps & { value: number }): React.ReactElement | null {
    const { scales } = useChartLayout()
    return (
        <VerticalStripe
            x={scales.y(value)}
            fillBefore={fillSide === 'below'}
            fillAfter={fillSide === 'above'}
            {...rest}
        />
    )
}

/** The shared shell: optional filled half-plane, the stroked line, and an optional label.
 *  Sub-components compute the pixel geometry; this just paints it. */
function ReferenceLineView({
    fillRect,
    fillColor,
    fillOpacity,
    lineStyle,
    hitAreaStyle,
    label,
    valueText,
    labelStyle,
}: {
    fillRect: React.CSSProperties | null
    fillColor: string
    fillOpacity: number
    lineStyle: React.CSSProperties
    hitAreaStyle: React.CSSProperties | null
    label: string | undefined
    valueText: string | undefined
    labelStyle: React.CSSProperties
}): React.ReactElement {
    const { theme } = useChartLayout()
    const [hovered, setHovered] = useState(false)
    const resolvedLabelStyle: React.CSSProperties = {
        ...labelStyle,
        backgroundColor: theme.tooltipBackground ?? TOOLTIP_FALLBACK_BG,
        color: theme.tooltipColor ?? TOOLTIP_FALLBACK_COLOR,
    }
    // Hovering the line (or its label) reveals the value, appended to its text label (or shown alone).
    const content = hovered && valueText ? (label ? `${label}: ${valueText}` : valueText) : label
    // The overlay layer is pointer-events-none, so anything that needs to catch hover must opt back in.
    const setHoveredOn = (): void => setHovered(true)
    const setHoveredOff = (): void => setHovered(false)
    return (
        <>
            {fillRect && (
                <div
                    className="absolute pointer-events-none"
                    style={{ ...fillRect, backgroundColor: fillColor, opacity: fillOpacity }}
                />
            )}
            <div data-attr="hog-chart-reference-line" className="absolute pointer-events-none" style={lineStyle} />
            {content && (
                <div
                    data-attr="hog-chart-reference-line-label"
                    className="absolute pointer-events-auto whitespace-nowrap font-medium text-[11px] rounded px-1 py-0.5 cursor-default"
                    style={resolvedLabelStyle}
                    onMouseEnter={setHoveredOn}
                    onMouseLeave={setHoveredOff}
                >
                    {content}
                </div>
            )}
            {/* Rendered last so the transparent hover target sits above the line and label, catching
                hover anywhere along the line — the label reads through it since it has no background. */}
            {hitAreaStyle && (
                <div
                    data-attr="hog-chart-reference-line-hit-area"
                    className="absolute pointer-events-auto"
                    style={hitAreaStyle}
                    onMouseEnter={setHoveredOn}
                    onMouseLeave={setHoveredOff}
                />
            )}
        </>
    )
}
